import { useEffect, useRef, useState } from "react"
import { Flex, Form, Checkbox, message } from "antd"
import { QuestionCircleOutlined } from "@ant-design/icons"
import PhoneInput from "react-phone-input-2"
import 'react-phone-input-2/lib/style.css'
import { useParams } from "react-router-dom"
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { getPreviousDataByValue, getPublicPreviousDataByValue } from "../../../services/requestServices";

const toDigits = (value) => String(value ?? "").replace(/\D/g, "")

const parseBound = (value) => {
    if (value === undefined || value === null || value === "") return undefined
    const num = Number(value)
    return Number.isFinite(num) ? num : undefined
}

const nationalDigits = (phone, dialCode) => {
    const digits = toDigits(phone)
    const code = toDigits(dialCode)
    if (code && digits.startsWith(code)) {
        return digits.slice(code.length)
    }
    return digits
}

const capNationalDigits = (phone, dialCode, maxDigits) => {
    const digits = toDigits(phone)
    const code = toDigits(dialCode)
    if (!code) return digits
    const local = nationalDigits(digits, code)
    if (maxDigits === undefined || local.length <= maxDigits) {
        return digits
    }
    return `${code}${local.slice(0, maxDigits)}`
}

const ContactInput = (props) => {
    const { spaceId, requestId } = useParams()
    const { label, tooltip, helpText, mandatory, readOnly, id, form, previewMode, previewmode, setValueData, formValues, placeholder = "", countryCode, enablePreviousDataFetch, requestDetail, isPublicForm = false, minValue, maxValue } = props
    const minDigits = parseBound(minValue)
    const maxDigits = parseBound(maxValue)
    const isPreview = previewMode || previewmode;
    const isEditing = !!(requestDetail && Object.keys(requestDetail).length > 0);
    const [dialCode, setDialCode] = useState("")
    const [phoneValue, setPhoneValue] = useState(() => formValues?.[id] || "")
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;
    const disabled = previewMode && readOnly ? true : false
    const contactValue = phoneValue
    const dialCodeRef = useRef(dialCode)
    const lastIncomingRef = useRef(formValues?.[id])
    dialCodeRef.current = dialCode

    const applyPhoneValue = (phone, nextDialCode = dialCodeRef.current) => {
        const nextPhone = capNationalDigits(phone, nextDialCode, maxDigits)
        lastIncomingRef.current = nextPhone
        setPhoneValue(nextPhone)
        form?.setFieldValue(id, nextPhone)
        return nextPhone
    }

    const validateCharacters = (_, value) => {
        if (!value || !String(value).trim()) {
            return Promise.resolve();
        }

        const digitsLength = nationalDigits(value, dialCodeRef.current).length
        if (minDigits !== undefined && digitsLength < minDigits) {
            return Promise.reject(new Error(`Minimum ${minDigits} digits required`));
        }
        if (maxDigits !== undefined && digitsLength > maxDigits) {
            return Promise.reject(new Error(`Maximum ${maxDigits} digits allowed excluding country code`));
        }
        return Promise.resolve();
    };
    const rules = [{ required: mandatory ? true : false }, { validator: validateCharacters }];

    const handleContactChange = (phone, country) => {
        const nextDialCode = country?.dialCode || dialCodeRef.current
        if (country?.dialCode) {
            setDialCode(country.dialCode)
            dialCodeRef.current = country.dialCode
        }
        applyPhoneValue(phone, nextDialCode)
    }

    const fetchPreviousData = async (value) => {
        const response = isPublicForm
            ? await getPublicPreviousDataByValue(id, value, props.requestId, props.spaceId, props.shareToken)
            : await getPreviousDataByValue(id, value, spaceId, requestId);
        const fetchedValues = response?.data?.value;
        if (response.status === 200 && fetchedValues && typeof fetchedValues === "object") {
            const fieldsToSet = { ...fetchedValues };
            delete fieldsToSet[id];
            if (Object.keys(fieldsToSet).length > 0) {
                form?.setFieldsValue(fieldsToSet);
            }
        }
    };

    const handleContactBlur = () => {
        const value = form?.getFieldValue(id);
        if (enablePreviousDataFetch && isPreview && !isEditing && value) {
            fetchPreviousData(value);
        }
    };
    let activeSetValue = getActiveSetValue(setValueData)

    // Track the condition value already applied so an editable field is prefilled
    // only once. Disabled fields stay locked to the condition value.
    const lastAppliedConditionalRef = useRef(undefined)

    useEffect(() => {
        const incoming = formValues?.[id]
        if (incoming === lastIncomingRef.current) return
        lastIncomingRef.current = incoming
        if (incoming !== undefined && incoming !== null) {
            setPhoneValue(incoming)
        }
    }, [formValues, id])

    useEffect(() => {
        if (activeSetValue?.value) {
            const key = capNationalDigits(activeSetValue.value, dialCodeRef.current, maxDigits)
            if (disabled) {
                const current = form?.getFieldValue(id)
                if (current !== key) {
                    applyPhoneValue(key)
                }
            } else if (lastAppliedConditionalRef.current !== key) {
                applyPhoneValue(key)
            }
            lastAppliedConditionalRef.current = key
        } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: form?.getFieldValue(id), appliedValue: lastAppliedConditionalRef.current })) {
            applyPhoneValue(getConditionalRevertValue(id, props.defaultFormValues) || "")
            lastAppliedConditionalRef.current = undefined
        } else {
            lastAppliedConditionalRef.current = undefined
        }
    }, [activeSetValue, disabled, form, id])

    return (
        <div className="form-element">
            <Flex vertical className="contact-field">
                <Form.Item name={id} label={label} tooltip={help} rules={rules} valuePropName="checked" style={{ width: "100%" }}>
                    <PhoneInput disabled={disabled} placeholder={placeholder} value={contactValue} country={countryCode || 'in'} disableDropdown={!!countryCode} countryCodeEditable={!countryCode} specialLabel="" enableSearch={true} onChange={handleContactChange} inputProps={{ onBlur: handleContactBlur }} />
                </Form.Item>
            </Flex>
        </div>
    )
}

export default ContactInput